import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import { Button, Grid, TextField } from "@mui/material";
import { toast } from "react-toastify";

import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useLazyGetDDLDriverListQuery } from "store/services/driverApi";
import { useAuth } from "hooks";
import { getDateTimeAuto } from "utils/dateFormats";
import { useLazySearchConsolidationsQuery } from "store/services/consolidationApi";
import { useLazyGetDDLBranchListQuery } from "store/services/branchApi";

const INITIAL_STATE = {
  account: null,
  entity: null,
  consolidationId: null,
  shipmentId: [],
  vehicle_id: null,
  driverId: null,
  loadedBranch: null,
  loadingTime: getDateTimeAuto(),
  loadingLocation: { lat: "", lon: "" },
  isActive: true,
};

const ShipmentLoadingForm = ({
  isBusy = null,
  shipmentLoadingDetail = null,
  onSubmit = () => { },
}) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  // Local state for search
  const [consolidationSearchQuery, setConsolidationSearchQuery] = useState("");
  const [shipmentSearchQuery, setShipmentSearchQuery] = useState("");
  const [consolidationOptions, setConsolidationOptions] = useState([]);
  const [shipmentOptions, setShipmentOptions] = useState([]);
  const [initialConsolidationsFetched, setInitialConsolidationsFetched] = useState(false);

  // React Hook Form
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity || null,
      account: viewEntity?.entity?.account || null,
      consolidationId: state?.consolidation || null,
      shipmentId: state?.shipments || [],
    },
  });

  // Watch form fields
  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchAccount = useWatch({ control, name: "account" });
  const _watchConsolidationId = useWatch({ control, name: "consolidationId" });
  const _watchDriver = useWatch({ control, name: "driverId" });

  // RTK Queries
  const { data: accounts, isLoading: loadingAccounts } = useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] = useLazyGetDDLEntityListQuery();
  const [searchConsolidations, { isLoading: loadingSearchResults }] = useLazySearchConsolidationsQuery();
  const [fetchDrivers, { data: drivers, isLoading: loadingDrivers }] = useLazyGetDDLDriverListQuery();
  const [fetchBranches, { data: branches, isLoading: loadingBranches }] =
    useLazyGetDDLBranchListQuery();

  // Derived state for vehicle options
  const selectedVehicles = _watchDriver?.assigned_vehicle_ids || [];

  // Fetch initial 100 consolidations when entity is available
  const fetchInitialConsolidations = async () => {
    if (!_watchAccount?._id || !_watchEntity?._id) return;
    try {
      const result = await searchConsolidations({ query: "" }).unwrap();
      const consolidations = result?.data || [];
      setConsolidationOptions(consolidations);
      const allShipments = consolidations.flatMap((consolidation) => {
        return (consolidation.shipmentId || []).map((shipment) => ({
          ...shipment,
          _consolidationId: consolidation._id,
          _consolidationData: consolidation,
        }));
      });
      setShipmentOptions(allShipments);
      setInitialConsolidationsFetched(true);
    } catch (error) {
      console.error("Error fetching initial consolidations:", error);
      setConsolidationOptions([]);
      setShipmentOptions([]);
    }
  };

  // Search for consolidations by consolidationId
  const handleConsolidationSearch = async (query) => {
    if (!query || query.length < 5) {
      if (initialConsolidationsFetched) {
        fetchInitialConsolidations();
      }
      return;
    }

    if (!_watchAccount?._id || !_watchEntity?._id) {
      toast.error("Please select account and entity first");
      return;
    }

    try {
      const result = await searchConsolidations({ query: query.trim() }).unwrap();

      setConsolidationOptions(result?.data || []);
    } catch (error) {
      console.error("Error searching consolidations:", error);
      setConsolidationOptions([]);
    }
  };

  // Search for shipments by shipmentId
  const handleShipmentSearch = async (query) => {
    if (!query || query.length < 5) {
      if (initialConsolidationsFetched) {
        fetchInitialConsolidations();
      }
      return;
    }

    if (!_watchAccount?._id || !_watchEntity?._id) {
      toast.error("Please select account and entity first");
      return;
    }

    try {
      const result = await searchConsolidations({ query: query.trim() }).unwrap();

      const allShipments = (result?.data || []).flatMap((consolidation) => {
        return (consolidation.shipmentId || []).map((shipment) => ({
          ...shipment,
          _consolidationId: consolidation._id,
          _consolidationData: consolidation,
        }));
      });

      setShipmentOptions(allShipments);
    } catch (error) {
      console.error("Error searching shipments:", error);
      setShipmentOptions([]);
    }
  };

  // Handle consolidation selection
  const handleConsolidationSelection = (selectedConsolidation) => {
    if (!selectedConsolidation) {
      setValue("consolidationId", null);
      setValue("shipmentId", []);
      return;
    }

    setValue("consolidationId", selectedConsolidation);

    const consolidationShipments = selectedConsolidation.shipmentId || [];
    setValue("shipmentId", consolidationShipments);
  };

  // Handle shipment selection
  const handleShipmentSelection = (selectedShipments) => {
    if (!selectedShipments || selectedShipments.length === 0) {
      setValue("shipmentId", []);
      return;
    }

    if (_watchConsolidationId) {
      setValue("shipmentId", selectedShipments);
      return;
    }

    const hasConsolidationData = selectedShipments.some(shipment => shipment._consolidationId);

    if (hasConsolidationData) {
      const firstShipmentWithConsolidation = selectedShipments.find(s => s._consolidationId);
      const consolidationFromShipment = firstShipmentWithConsolidation._consolidationData;
      const consolidationIdFromShipment = firstShipmentWithConsolidation._consolidationId;

      const allFromSameConsolidation = selectedShipments
        .filter(shipment => shipment._consolidationId)
        .every((shipment) => shipment._consolidationId === consolidationIdFromShipment);

      if (!allFromSameConsolidation) {
        toast.error("Cannot select shipments from different consolidations");
        return;
      }

      setValue("consolidationId", consolidationFromShipment);

      const cleanShipments = selectedShipments.map((shipment) => {
        const { _consolidationId, _consolidationData, ...cleanShipment } = shipment;
        return cleanShipment;
      });

      setValue("shipmentId", cleanShipments);
    } else {
      setValue("shipmentId", selectedShipments);
    }
  };

  // Available shipments based on selected consolidation
  const consolidationShipments = React.useMemo(() => {
    if (_watchConsolidationId?.shipmentId) {
      return _watchConsolidationId.shipmentId || [];
    }
    return shipmentOptions;
  }, [_watchConsolidationId, shipmentOptions]);

  // Fetch initial 100 consolidations when entity is set and page loads
  useEffect(() => {
    if (_watchAccount?._id && _watchEntity?._id && !shipmentLoadingDetail && !initialConsolidationsFetched) {
      fetchInitialConsolidations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_watchAccount?._id, _watchEntity?._id, shipmentLoadingDetail]);

  // Auto-populate from state
  useEffect(() => {
    if (state?.consolidation) {
      setValue("consolidationId", state.consolidation);
      if (state.consolidation.shipmentId) {
        setValue("shipmentId", state.consolidation.shipmentId);
      }
    }

    if (state?.shipments?.length) {
      setValue("shipmentId", state.shipments);
    }
  }, [state, setValue]);

  // Fetch drivers and branches based on account and entity
  useEffect(() => {
    if (!_watchEntity?._id) return;
    fetchDrivers({ account: _watchAccount?._id, entity: _watchEntity?._id });
    fetchBranches({ account: _watchAccount?._id, entity: _watchEntity?._id });
  }, [_watchAccount?._id, _watchEntity?._id, fetchDrivers, fetchBranches]);

  // Fetch entities when account changes
  useEffect(() => {
    if (isAccountAdmin || isSystemAdmin) {
      fetchEntities({
        account: _watchAccount?._id ?? viewEntity?.entity?.account?._id,
      });
    }
  }, [
    _watchAccount?._id,
    fetchEntities,
    isAccountAdmin,
    isSystemAdmin,
    viewEntity?.entity?.account,
  ]);

  // Reset initial consolidations when entity changes
  useEffect(() => {
    if (_watchEntity?._id) {
      setInitialConsolidationsFetched(false);
      setConsolidationOptions([]);
      setShipmentOptions([]);
    }
  }, [_watchEntity?._id]);

  // Reset form when editing
  useEffect(() => {
    if (!shipmentLoadingDetail) return;

    const consolidationIdValue =
      shipmentLoadingDetail?.consolidationId?._id ||
      shipmentLoadingDetail?.consolidationId ||
      null;

    reset({
      ...INITIAL_STATE,
      ...shipmentLoadingDetail,
      loadingTime: shipmentLoadingDetail?.loadingTime
        ? new Date(shipmentLoadingDetail.loadingTime).toISOString().slice(0, 16)
        : getDateTimeAuto(),
      consolidationId: consolidationIdValue
        ? { _id: consolidationIdValue }
        : null,
      shipmentId:
        shipmentLoadingDetail?.shipmentId?.map((id) =>
          typeof id === "string" ? { _id: id } : id
        ) || [],
      account: shipmentLoadingDetail?.account || null,
      entity: shipmentLoadingDetail?.entity || null,
      vehicle_id: shipmentLoadingDetail?.vehicle_id || null,
      driverId: shipmentLoadingDetail?.driverId || null,
      loadedBranch: shipmentLoadingDetail?.loadedBranch || null,
    });

    const accountId =
      shipmentLoadingDetail?.account?._id ||
      shipmentLoadingDetail?.account ||
      "";
    if (accountId && (isAccountAdmin || isSystemAdmin)) {
      fetchEntities({ account: accountId });
    }

    const entityId =
      shipmentLoadingDetail?.entity?._id || shipmentLoadingDetail?.entity || "";
    if (entityId) {
      fetchDrivers({ account: accountId, entity: entityId });
      fetchBranches({ account: accountId, entity: entityId });
    }
  }, [
    shipmentLoadingDetail,
    reset,
    fetchEntities,
    fetchDrivers,
    fetchBranches,
    isAccountAdmin,
    isSystemAdmin,
  ]);

  // Methods
  const handleOnLoadingSubmit = (data) => {
    const _loadingPayload = {
      ...data,
      entity: data?.entity?._id,
      account: data?.account?._id,
      consolidationId: data?.consolidationId?._id || "",
      shipmentId: Array.isArray(data?.shipmentId)
        ? data?.shipmentId.map((id) => id._id)
        : [],
      vehicle_id: data?.vehicle_id?._id,
      driverId: data?.driverId?._id,
      loadedBranch: data?.loadedBranch?._id || null,
    };
    onSubmit(_loadingPayload);
  };

  return (
    <>
      <AppForm loading={isBusy}>
        <AppForm.Content>
          <Grid container spacing={4}>
            {(isAccountAdmin || isSystemAdmin) && (
              <Grid item md={6}>
                <Controller
                  name="account"
                  control={control}
                  rules={{ required: "Please select an account." }}
                  render={({ field }) => (
                    <BaseAutocomplete
                      {...field}
                      fullWidth
                      label="Account"
                      required
                      value={field.value ?? null}
                      onChange={(_, data) => {
                        field.onChange(data);
                        setValue("entity", null);
                        setValue("consolidationId", null);
                        setValue("shipmentId", []);
                        setValue("driverId", null);
                        setValue("vehicle_id", null);
                        setConsolidationOptions([]);
                        setShipmentOptions([]);
                        setInitialConsolidationsFetched(false);
                      }}
                      getOptionLabel={(option) => option?.name ?? ""}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id || value === null
                      }
                      options={accounts || []}
                      loading={loadingAccounts}
                      loadingText="Loading Account..."
                      disabled={isAccountAdmin}
                      helperText={errors?.account?.message}
                      error={!!errors?.account}
                    />
                  )}
                />
              </Grid>
            )}
            {(isAccountAdmin || isSystemAdmin) && (
              <Grid item md={6}>
                <Controller
                  name="entity"
                  control={control}
                  rules={{ required: "Please select an entity." }}
                  render={({ field: { value, onChange } }) => (
                    <BaseAutocomplete
                      fullWidth
                      required
                      label="Entity"
                      value={value ?? null}
                      onChange={(_, data) => {
                        onChange(data);
                        setValue("consolidationId", null);
                        setValue("shipmentId", []);
                        setValue("driverId", null);
                        setValue("vehicle_id", null);
                        setConsolidationOptions([]);
                        setShipmentOptions([]);
                        setInitialConsolidationsFetched(false);
                      }}
                      getOptionLabel={(option) => option?.name ?? ""}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id || value === null
                      }
                      options={entities || []}
                      loading={loadingEntities}
                      loadingText="Loading Entities..."
                      helperText={errors?.entity?.message}
                      error={!!errors?.entity}
                    />
                  )}
                />
              </Grid>
            )}
            {!shipmentLoadingDetail && (
              <Grid item xs={12} sm={6}>
                <Controller
                  name="consolidationId"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseAutocomplete
                      fullWidth
                      label="Consolidation"
                      value={value ?? null}
                      inputValue={consolidationSearchQuery}
                      onInputChange={(_, newInputValue) => {
                        setConsolidationSearchQuery(newInputValue);
                        if (newInputValue.length >= 5) {
                          handleConsolidationSearch(newInputValue);
                        } else if (newInputValue.length === 0) {
                          fetchInitialConsolidations();
                        }
                      }}
                      onChange={(_, data) => {
                        onChange(data);
                        handleConsolidationSelection(data);
                      }}
                      getOptionLabel={(option) =>
                        option?.consolidationId && option?.name
                          ? `${option.consolidationId} - ${option.name}`
                          : option?.consolidationId || ""
                      }
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id || value === null
                      }
                      options={consolidationOptions}
                      loading={loadingSearchResults}
                      loadingText="Loading..."
                      helperText={
                        errors?.consolidationId?.message ||
                        "Type 5+ characters to search or select from list"
                      }
                      error={!!errors?.consolidationId}
                      disabled={!!state?.consolidation}
                    />
                  )}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <Controller
                name="shipmentId"
                control={control}
                rules={{ required: "Please select at least one Shipment ID." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    multiple
                    label="Shipment ID"
                    required
                    value={value || []}
                    inputValue={shipmentSearchQuery}
                    onInputChange={(_, newInputValue) => {
                      setShipmentSearchQuery(newInputValue);
                      if (newInputValue.length >= 5 && !_watchConsolidationId) {
                        handleShipmentSearch(newInputValue);
                      } else if (newInputValue.length === 0 && !_watchConsolidationId) {
                        fetchInitialConsolidations();
                      }
                    }}
                    onChange={(_, data) => {
                      onChange(data);
                      handleShipmentSelection(data);
                    }}
                    getOptionLabel={(option) =>
                      option?.shipmentId
                        ? option.IsReturning
                          ? `${option.shipmentId} (R)`
                          : option.shipmentId
                        : ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={consolidationShipments}
                    loading={loadingSearchResults}
                    loadingText="Loading..."
                    helperText={
                      errors?.shipmentId?.message ||
                      (_watchConsolidationId
                        ? "Select from consolidated shipments"
                        : "Type at least 5 characters to search or select from list")
                    }
                    error={!!errors?.shipmentId}
                    disabled={!!state?.shipments}
                  />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <Controller
                name="loadingTime"
                control={control}
                rules={{ required: "Loading Time is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Loading Time"
                    type="datetime-local"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.loadingTime}
                    helperText={errors?.loadingTime?.message}
                  />
                )}
              />
            </Grid>
            <Grid item md={6}>
              <Controller
                name="driverId"
                control={control}
                rules={{ required: "Please select Driver ID." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    label="Driver"
                    required
                    value={value ?? null}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("vehicle_id", null);
                    }}
                    getOptionLabel={(option) =>
                      option && `${option?.driverId} - ${option?.name}`
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id || value === null
                    }
                    options={drivers || []}
                    loading={loadingDrivers}
                    loadingText="Loading Drivers..."
                    helperText={errors?.driverId?.message}
                    error={!!errors?.driverId}
                    disabled={loadingDrivers}
                  />
                )}
              />
            </Grid>
            <Grid item md={6}>
              <Controller
                name="vehicle_id"
                control={control}
                rules={{ required: "Please select Vehicle ID." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    label="Vehicle"
                    required
                    value={value ?? null}
                    onChange={(_, data) => onChange(data)}
                    getOptionLabel={(option) =>
                      option && option.vehicle_id && option.registration_number
                        ? `${option.vehicle_id} - ${option.registration_number}`
                        : ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id || value === null
                    }
                    options={selectedVehicles}
                    disabled={!_watchDriver || loadingDrivers}
                    loadingText="Loading Vehicles..."
                    helperText={errors?.vehicle_id?.message}
                    error={!!errors?.vehicle_id}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="loadedBranch"
                control={control}
                rules={{ required: "Please select the loaded branch." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    label="Loaded Branch"
                    required
                    value={value ?? null}
                    onChange={(_, data) => onChange(data)}
                    getOptionLabel={(option) =>
                      option
                        ? `${option?.name ?? ""} (${option?.code ?? ""})`.trim()
                        : ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id || value === null
                    }
                    options={branches || []}
                    loading={loadingBranches}
                    loadingText="Loading Branches..."
                    helperText={errors?.loadedBranch?.message}
                    error={!!errors?.loadedBranch}
                  />
                )}
              />
            </Grid>
            {shipmentLoadingDetail && (
              <Grid item xs={6}>
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field }) => (
                    <AppSwitch {...field} label="Is Active?" />
                  )}
                />
              </Grid>
            )}
          </Grid>
        </AppForm.Content>
        <AppForm.Actions>
          <Button onClick={() => navigate(-1)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit(handleOnLoadingSubmit)}
          >
            {shipmentLoadingDetail ? "Update" : "Add"}
          </Button>
        </AppForm.Actions>
      </AppForm>
    </>
  );
};

export default ShipmentLoadingForm;